import {
  Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output, ViewEncapsulation
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';

import { EcommerceConfigService } from 'idt-ecommerce-api/config/ecommerce-config.service';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { RenderSetupData, RenderSetupDataService } from 'idt-ecommerce-api/render-setup-data/render-setup-data.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { DynamicIdtComponentService } from 'shared/dynamic/dynamic-idt-component.service';
import { renderComponents } from 'shared/idt-components/idt-components-render.module';
import { IdtComponentsService } from 'shared/idt-components/idt-components.service';

import { CMSEvent, CMSEventType } from './models/cms-event.model';
import { RenderEvent } from './render-events/render-events.model';
import { RenderEventsService } from './render-events/render-events.service';
import { RenderConfiguration } from './render.configuration';
import { CMSRenderEventsService } from './services/cms-render-events.service';
import { RenderConfigurationService } from './services/render-configuration.service';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from './services/render-growthbook-configs/render-growthbook-configs.service';
import { RenderSlugService } from './services/render-slug.service';
import { RenderStateService } from './services/render-state/render-state.service';
import { TenantVariablesService } from './services/tenant-variables/tenant-variables.service';


const CMS_TENANT_VARIABLES_UPDATED = 'CMS_TENANT_VARIABLES_UPDATED';

interface InitialConfiguration {
  renderConfiguration?: RenderConfiguration;
  setupData?: RenderSetupData;
  userSegments?: string[];
  /**
   * @deprecated Pass Growthbook configurations via `setGrowthbookConfigurations()` instead.
   * This field is kept for backwards compatibility only and will be removed in a future release.
   */
  growthbookConfigs?: GrowthbookConfig[];
}

interface CmsWebComponent extends HTMLElement {
  launch: (config?: InitialConfiguration) => void;
  setGrowthbookConfigurations: (configs: GrowthbookConfig[]) => void;
}

@Component({
  selector: 'render-idt',
  template: `
  <render-page></render-page>
  `,
  /* https://jira.inditex.com/jira/browse/MECFRONT-1539
   * In case of needing to access DOM elements from outside Angular, it will be necessary to remove this line when
   * generating the web component.
   * This is due to the fact that an inner element of the shadow DOM cannot be queried.
   * It can also be accessed without removing the encapsulation with
   * querySelector('render-idt').shadowRoot.querySelector('my-element').
   * */
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrls: [
    './render-page.component.scss'
  ],
  providers: [RenderSlugService]
})
export class RenderComponent extends UnsubscribeComponent implements OnInit, OnDestroy {

  @Input() public autoload?: 'true' | 'false' = 'true';
  @Input() public slug!: string;
  @Input() public country!: string;
  @Input() public versionid!: string;
  @Input() public lang!: string;
  @Input() public userflags: string | undefined;
  @Input() public timetravelto: string | undefined;
  @Input() public config?: string;
  @Input() public timestamp?: string;
  @Output() public idtEvent = new EventEmitter<RenderEvent>();
  @Output() public cmsEvent = new EventEmitter<CMSEvent<CMSEventType>>();

  constructor(
    private readonly elementRef: ElementRef<CmsWebComponent>,
    private readonly contextService: ContextService,
    private readonly ecommerceConfigService: EcommerceConfigService,
    private readonly tenantVariablesService: TenantVariablesService,
    private readonly idtComponentsService: IdtComponentsService,
    private readonly dynamicIdtComponentService: DynamicIdtComponentService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly renderStateService: RenderStateService,
    private readonly renderSetupDataService: RenderSetupDataService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly ngZone: NgZone,
    private readonly renderSlugService: RenderSlugService
  ) {
    super();
    // This is needed in order to expose the launch method to the outside
    this.elementRef.nativeElement.launch = this.launch.bind(this);
    this.elementRef.nativeElement.setGrowthbookConfigurations = this.setGrowthbookConfigurations.bind(this);
  }

  public ngOnInit(): void {
    this.parseTenantVars();

    this.renderEventsService.events.pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((linkEvent) => {
      linkEvent.event.preventDefault();
      this.idtEvent.next(linkEvent);
    });

    this.cmsRenderEventsService.cmsEvent.pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((event) => {
      this.cmsEvent.next(event);
    });

    this.dynamicIdtComponentService.setComponents(renderComponents);
    this.dynamicIdtComponentService.setDefinitions(this.idtComponentsService.componentsDefinition);
    this.renderSlugService.setSlug(this.slug);

    if (this.timestamp) {
      this.contextService.setStoreTimestamp(this.timestamp);
    }

    if (this.autoload !== 'false') {
      this.launch(
        this.config ? { renderConfiguration: JSON.parse(this.config) as RenderConfiguration } : undefined
      );
    }
  }

  public setGrowthbookConfigurations(configs: GrowthbookConfig[]): void {
    this.renderGrowthbookService.setConfigurations(configs);
  }

  public launch(config?: InitialConfiguration): void {
    this.renderConfigurationService.merge(config?.renderConfiguration);

    this.renderSetupDataService.setupData = config?.setupData;

    if (config?.growthbookConfigs !== undefined) {
      console.warn(
        '[render-idt] The `growthbookConfigs` field in launch() is deprecated and will be removed in a future release.'
        + ' Use `setGrowthbookConfigurations()` instead.'
      );
      this.renderGrowthbookService.setConfigurations(config.growthbookConfigs);
    }

    this.ecommerceConfigService.setConfig({
      ...this.renderConfigurationService.config.ecommerce,
      apiEcommerceBaseUrl: this.renderConfigurationService.config.apiEcommerceBaseUrl
    });

    this.ngZone.run(async () => {
      await this.renderStateService.initPage({
        slug: this.slug,
        country: this.country,
        versionId: this.versionid,
        lang: this.lang,
        timeTravelTo: this.timetravelto,
        userFlags: this.userflags,
        userSegments: config?.userSegments
      });
      this.contextService.init(this.country, this.lang);
    });
  }

  @HostListener(`window:${CMS_TENANT_VARIABLES_UPDATED}`)
  private syncTenantVars(): void {
    this.parseTenantVars();
  }

  private parseTenantVars(): void {
    const parsedVariables = localStorage.getItem('CMS_TENANT_VARIABLES') ?? '{}';
    this.tenantVariablesService.setTenantVariables(JSON.parse(parsedVariables) as Record<string, string>);
  }

}
